/**
 * Derives the per-field provenance map AtlasStructuredForm consumes from the
 * merge result's field_metadata. Only fields that were genuinely "merged"
 * (multiple sources, or a resolved conflict) get an entry, so single-doc and
 * trivial fields render no provenance UI at all.
 *
 * Note on is_user_modified: user-modified fields retain their provenance entry
 * so the UI can still show where the original value came from after an edit.
 */
import type { MergedFieldValue } from './documentSetService';

export interface FieldProvenance {
  sourceName: string;
  confidence: number;
  alternatives: Array<{ value: any; sourceName: string }>;
}

export function deriveFieldSources(
  fieldMetadata: Record<string, MergedFieldValue> | null | undefined
): Record<string, FieldProvenance> {
  const out: Record<string, FieldProvenance> = {};
  if (!fieldMetadata) return out;

  for (const [path, meta] of Object.entries(fieldMetadata)) {
    const primary = meta?.primary_source;
    if (!primary?.document_name) continue;

    const allSources = Array.isArray(meta.all_sources) ? meta.all_sources : [];
    const merged =
      allSources.length > 1 ||
      meta.has_conflict ||
      meta.is_auto_resolved ||
      meta.is_user_modified;
    if (!merged) continue;

    const conflictAlternatives = meta.conflict?.alternatives;
    let alternatives: Array<{ value: any; sourceName: string }>;

    if (Array.isArray(conflictAlternatives) && conflictAlternatives.length > 0) {
      // Use real per-source values from conflict data, excluding the primary source.
      alternatives = conflictAlternatives
        .filter((alt) => alt?.source?.document_name && alt.source.document_name !== primary.document_name)
        .map((alt) => ({ value: alt.value, sourceName: alt.source.document_name }));
    } else {
      // No conflict info available — per-source values are unavailable, so the
      // merged (winning) value is shown for all other sources as a fallback.
      alternatives = allSources
        .filter((s) => s?.document_name && s.document_name !== primary.document_name)
        .map((s) => ({ value: meta.value, sourceName: s.document_name }));
    }

    out[path] = {
      sourceName: primary.document_name,
      confidence: typeof primary.confidence === 'number' ? primary.confidence : 0,
      alternatives,
    };
  }
  return out;
}
